/** Positions as a survey sheet writes them: degrees, minutes and seconds
 *  with the hemisphere letter, to a thousandth of a second (about 3 cm),
 *  longitude degrees in three digits. The api stores decimal WGS 84 and
 *  converts a survey for the map; the browser converts only for display,
 *  a stored latitude and longitude shown as the UTM easting and northing
 *  Sensor Info's Properties speaks in. */
import type { Sensor } from '../api/types'

const pad = (n: number, width: number) => String(n).padStart(width, '0')

export function formatDms(value: number, axis: 'lat' | 'lon'): string {
  const letter = axis === 'lat' ? (value < 0 ? 'S' : 'N') : (value < 0 ? 'W' : 'E')
  // Count in thousandths of a second so a rounding carry runs through the
  // minutes and degrees instead of printing 60.000″.
  const total = Math.round(Math.abs(value) * 3600 * 1000)
  const degrees = Math.floor(total / 3_600_000)
  const minutes = Math.floor((total % 3_600_000) / 60_000)
  const millis = total % 60_000
  const seconds = `${pad(Math.floor(millis / 1000), 2)}.${pad(millis % 1000, 3)}`
  return `${letter} ${pad(degrees, axis === 'lat' ? 2 : 3)}° ${pad(minutes, 2)}′ ${seconds}″`
}

export function formatMetres(value: number): string {
  return `${value.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m`
}

export function formatPosition(position: Sensor['position'], elevation: number | null): string {
  if (!position) return 'Coordinates not set'
  const where = `${formatDms(position.lat, 'lat')}, ${formatDms(position.lon, 'lon')}`
  if (position.source === 'utm') return `${where}, from its UTM survey`
  return elevation === null ? where : `${where}, elevation ${formatMetres(elevation)}`
}

// WGS 84 and the UTM projection, both ways, with Krüger's series to the
// sixth order in n (Karney 2011, eqs 35 and 36): nanometres across a zone,
// so the three decimals Properties shows are the conversion's own and not
// its error. services/geo.py inverts for the map with the series to the
// third order, millimetres across a zone. Northern hemisphere only: Georgia
// is in it, and a southern grid needs its own false northing and EPSG code.
const A = 6378137
const F = 1 / 298.257223563
const K0 = 0.9996
const FALSE_EASTING = 500_000
const N = F / (2 - F)
const E2 = F * (2 - F)
const E = Math.sqrt(E2)
const RECT = (A / (1 + N)) * (1 + N ** 2 / 4 + N ** 4 / 64 + N ** 6 / 256)
const [N2, N3, N4, N5, N6] = [N ** 2, N ** 3, N ** 4, N ** 5, N ** 6]
const ALPHA = [
  N / 2 - (2 / 3) * N2 + (5 / 16) * N3 + (41 / 180) * N4 - (127 / 288) * N5 + (7891 / 37800) * N6,
  (13 / 48) * N2 - (3 / 5) * N3 + (557 / 1440) * N4 + (281 / 630) * N5 - (1983433 / 1935360) * N6,
  (61 / 240) * N3 - (103 / 140) * N4 + (15061 / 26880) * N5 + (167603 / 181440) * N6,
  (49561 / 161280) * N4 - (179 / 168) * N5 + (6601661 / 7257600) * N6,
  (34729 / 80640) * N5 - (3418889 / 1995840) * N6,
  (212378941 / 319334400) * N6,
]
const BETA = [
  N / 2 - (2 / 3) * N2 + (37 / 96) * N3 - (1 / 360) * N4 - (81 / 512) * N5 + (96199 / 604800) * N6,
  (1 / 48) * N2 + (1 / 15) * N3 - (437 / 1440) * N4 + (46 / 105) * N5 - (1118711 / 3870720) * N6,
  (17 / 480) * N3 - (37 / 840) * N4 - (209 / 4480) * N5 + (5569 / 90720) * N6,
  (4397 / 161280) * N4 - (11 / 504) * N5 - (830251 / 7257600) * N6,
  (4583 / 161280) * N5 - (108847 / 3991680) * N6,
  (20648693 / 638668800) * N6,
]
const toRadians = (deg: number) => (deg * Math.PI) / 180
const toDegrees = (rad: number) => (rad * 180) / Math.PI
const centralMeridian = (zone: number) => toRadians(zone * 6 - 183)

/** tan of the conformal latitude, from tan of the geodetic one. */
function conformal(tau: number): number {
  const sigma = Math.sinh(E * Math.atanh((E * tau) / Math.hypot(1, tau)))
  return tau * Math.hypot(1, sigma) - sigma * Math.hypot(1, tau)
}

export interface UtmPoint { easting: number; northing: number; zone: number }

/** The standard 6° zone a longitude falls in: Georgia's 36° to 42° E is
 *  zone 37 (EPSG:32637), 42° to 48° E zone 38 (EPSG:32638). A longitude on
 *  a boundary belongs to the zone east of it. */
export function utmZoneFor(longitude: number): number {
  const lon = ((((longitude + 180) % 360) + 360) % 360) - 180
  return Math.min(60, Math.floor((lon + 180) / 6) + 1)
}

/** The zone a northern UTM EPSG code names (32601 to 32660), or null for
 *  any other grid, WGS 84 in degrees (4326) among them. */
export function utmZoneOf(epsg: number | null): number | null {
  return epsg !== null && Number.isInteger(epsg) && epsg >= 32601 && epsg <= 32660 ? epsg - 32600 : null
}

export const utmEpsg = (zone: number): number => 32600 + zone

/** A latitude and longitude in decimal WGS 84 as a northern UTM easting and
 *  northing in metres, in the zone given (the sensor's own grid) or else
 *  the one its longitude falls in. Null outside UTM's northern band, 0° to
 *  84° N, where there is no such grid. */
export function wgs84ToUtm(latitude: number, longitude: number,
                           zone = utmZoneFor(longitude)): UtmPoint | null {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude < 0 || latitude > 84) return null
  const lambda = toRadians(longitude) - centralMeridian(zone)
  const tauP = conformal(Math.tan(toRadians(latitude)))
  const xiP = Math.atan2(tauP, Math.cos(lambda))
  const etaP = Math.asinh(Math.sin(lambda) / Math.hypot(tauP, Math.cos(lambda)))
  let xi = xiP
  let eta = etaP
  ALPHA.forEach((a, i) => {
    const j = 2 * (i + 1)
    xi += a * Math.sin(j * xiP) * Math.cosh(j * etaP)
    eta += a * Math.cos(j * xiP) * Math.sinh(j * etaP)
  })
  return { easting: FALSE_EASTING + K0 * RECT * eta, northing: K0 * RECT * xi, zone }
}

/** A northern UTM easting and northing in the zone given, as decimal WGS 84;
 *  the inverse of wgs84ToUtm. */
export function utmToWgs84(easting: number, northing: number, zone: number): { lat: number; lon: number } {
  const xi = northing / (K0 * RECT)
  const eta = (easting - FALSE_EASTING) / (K0 * RECT)
  let xiP = xi
  let etaP = eta
  BETA.forEach((b, i) => {
    const j = 2 * (i + 1)
    xiP -= b * Math.sin(j * xi) * Math.cosh(j * eta)
    etaP -= b * Math.cos(j * xi) * Math.sinh(j * eta)
  })
  const tauP = Math.sin(xiP) / Math.hypot(Math.sinh(etaP), Math.cos(xiP))
  // The geodetic latitude from the conformal one has no closed form; Newton
  // converges in two or three steps (Karney 2011, eqs 19 to 21). The cap
  // only guards against a non-finite input looping.
  let tau = tauP
  for (let step = 0; step < 10; step++) {
    const tauI = conformal(tau)
    const delta = ((tauP - tauI) / Math.hypot(1, tauI)) * (1 + (1 - E2) * tau * tau)
      / ((1 - E2) * Math.hypot(1, tau))
    tau += delta
    if (!(Math.abs(delta) > 1e-14)) break
  }
  const lambda = Math.atan2(Math.sinh(etaP), Math.cos(xiP))
  return { lat: toDegrees(Math.atan(tau)), lon: toDegrees(centralMeridian(zone) + lambda) }
}
